<script setup>
const props = defineProps({
    skeletonPlaceholderConfig: {
        type: Object,
        required: true,
        default: () => ({}),
        validator: (rawValue) => {
            const validations = {
                type: value => value === undefined || (typeof value === 'string' && ['image', 'text', 'headline', 'multi-headline', 'button'].includes(value)),
                iterator: value => value === undefined || typeof value === 'number',
                width: value => value === undefined || typeof value === 'string',
                height: value => value === undefined || typeof value === 'string',
                tag: value => value === undefined || typeof value === 'string',
                class: value => value === undefined || typeof value === 'string',
                length: value => value === undefined || typeof value === 'number'
            }

            return Object.entries(validations).every(([key, validation]) => validation(rawValue[key]))
        }
    }
})

// Set default value for count here
const skeletonPlaceholderConfig = computed(() => {
    return {
        ...props.skeletonPlaceholderConfig,
        iterator: props.skeletonPlaceholderConfig.iterator ?? 1,
        tag: props.skeletonPlaceholderConfig.tag ?? 'div'
    }
})

// Set style
const getStyle = computed(() => {
    let style = ''

    if (props.skeletonPlaceholderConfig.type === 'image') {
        style += `width: ${props.skeletonPlaceholderConfig.width}; height: ${props.skeletonPlaceholderConfig.height};`
    }

    if (props.skeletonPlaceholderConfig.length) {
        style += ` width: ${props.skeletonPlaceholderConfig.length}%;`
    }

    return style || false
})

const getSkeletonClass = computed(() => {
    return {
        [props.skeletonPlaceholderConfig.class]: props.skeletonPlaceholderConfig.class,
        'skeleton-placeholder-headline': props.skeletonPlaceholderConfig.type === 'headline',
        'skeleton-placeholder-multi-headline': props.skeletonPlaceholderConfig.type === 'multi-headline',
        'skeleton-placeholder-text': props.skeletonPlaceholderConfig.type === 'text',
        'skeleton-placeholder-standalone': !props.skeletonPlaceholderConfig.iterator,
        'skeleton-placeholder-image': props.skeletonPlaceholderConfig.type === 'image',
        'skeleton-placeholder-button': props.skeletonPlaceholderConfig.type === 'button'
    }
})
</script>

<template>
    <component
        :is="skeletonPlaceholderConfig.tag"
        v-for="index in skeletonPlaceholderConfig.iterator"
        :key="`skeleton-placeholder-${skeletonPlaceholderConfig.type}-${index}`"
        :class="getSkeletonClass"
        :style="getStyle"
        data-vitest="skeleton-placeholder"
    />
</template>

<style lang="scss">
.skeleton-placeholder-headline  {
    background: $skeleton-placeholder;
    position: relative;
    z-index: 1;
    border-radius: 10px;
    margin-block: 7px;
    width: 100%;
    height: 15px;
}

.skeleton-placeholder-text {
    width: 100%;

    &.skeleton-placeholder-standalone {
        &::after {
            margin-block: 7px;
        }
    }

    &::before {
        content: '';
        display: block;
        background: $skeleton-placeholder;
        width: 100%;
        height: 10px;
        position: relative;
        z-index: 1;
        border-radius: 10px;
        margin-block: 7px;
    }

    &::after {
        content: '';
        display: block;
        background: $skeleton-placeholder;
        width: 80%;
        height: 10px;
        position: relative;
        z-index: 1;
        border-radius: 10px;
        margin-block: 7px 14px;
    }
}

.skeleton-placeholder-multi-headline {
    width: 100%;

    &::before {
        content: '';
        display: block;
        background: $skeleton-placeholder;
        width: 100%;
        height: 15px;
        position: relative;
        z-index: 1;
        border-radius: 10px;
        margin-block: 10px;
    }

    &::after {
        content: '';
        display: block;
        background: $skeleton-placeholder;
        width: 80%;
        height: 15px;
        position: relative;
        z-index: 1;
        border-radius: 10px;
        margin-block: 10px;
    }
}

.skeleton-placeholder-image {
    background: $skeleton-placeholder;
    position: relative;
    z-index: 1;
    border-radius: 10px;
}

.skeleton-placeholder-button {
    background: $skeleton-placeholder;
    position: relative;
    z-index: 1;
    border-radius: 30px;
    height: 48px;
    width: 100%;
}
</style>
